import { Box, Spinner, Table, TableColumn, TableProps, Typography } from 'oskcomponents';
import { AdminAPI, Order, ProgramUser, getProgramId, noop } from 'oskcore';
import { TaskingOrder, TaskingOrderStatus } from 'oskcore/src/api/orders';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { StacAsset } from 'stac-ts';
import styled from 'styled-components';
import { DEFAULT_ORDER_STATUS_STYLE, OrderStatusMapEntry, TaskingOrderStatusMap } from '~/redux/modules/data/orders';
import { RootState } from '~/redux/store';
import { date_format_longer } from '~/utils';

export type TempOrderType = TaskingOrder & { assets: StacAsset[] };

const StyledTable = styled<any>(Table)`
    & {
        margin-top: 18px;
        border-radius: 0px !important;
    }

    thead {
        tr {
            th {
                overflow: hidden;
            }
            th:first-of-type {
                border-radius: 9px 0px 0px 9px;
            }
            th:last-of-type {
                border-radius: 0px 9px 9px 0px;
            }
        }
    }

    tbody tr:hover td,
    tbody .selected-row td {
        background-color: ${(props: any) => props.theme.colors[props.variant ?? 'primary'].highlight} !important;
    }

    tbody {
        tr {
            td {
                border-bottom: 1px solid ${(props: any) => props.theme.colors.black200};
            }
        }
    }

    th {
        background-color: #282828;
        color: white;
    }
`;

type ExternalOrderDashboardTableProps = {
    /** List of orders to display */
    orders: TaskingOrder[];
} & Omit<TableProps, 'data' | 'columns' | 'style'>;

const ExternalOrderDashboardTable = ({ orders, ...props }: ExternalOrderDashboardTableProps) => {
    const programId = getProgramId();

    const [selectedId, setSelectedId] = useState<string | undefined>();
    const [users, setUsers] = useState<ProgramUser[]>([]);

    useEffect(() => {
        AdminAPI.listProgramUsers({ program: programId }).then((response) => setUsers(response.data.results));
    }, []);

    const columns = React.useMemo<Array<TableColumn>>(
        () => [
            {
                Header: 'Order Name',
                accessor: 'properties.order_parameters.name',
                disableSortBy: true,
                width: '200px',
            },
            {
                Header: 'Description',
                accessor: 'properties.order_parameters.description',
                disableSortBy: true,

                width: '200px',
            },
            {
                Header: 'Created',
                accessor: 'properties.created',
                Cell: ({ value }: any) => {
                    return date_format_longer(value);
                },
                width: '200px',
            },
            {
                Header: 'Ordered By',
                accessor: 'properties.order_parameters.user_id',
                Cell: ({ value }: any) => {
                    return users.find((user) => user.id === value)?.email ?? <Spinner variant="Circle" size="Tiny" />;
                },
            },
            {
                Header: 'Status',
                accessor: 'properties.status',
                disableSortBy: true,
                width: '250px',
                Cell: ({ value }: any) => {
                    const status = value as TaskingOrderStatus;
                    let StatusDisplayStyle: OrderStatusMapEntry = DEFAULT_ORDER_STATUS_STYLE;
                    if (status.status_code in TaskingOrderStatusMap) {
                        StatusDisplayStyle = TaskingOrderStatusMap[status.status_code];
                    } else {
                        StatusDisplayStyle.name = status.status_code;
                    }
                    return (
                        <Box
                            style={{
                                color: StatusDisplayStyle.fg,
                                backgroundColor: StatusDisplayStyle.bg,
                                padding: '8px',
                                borderRadius: '8px',
                                width: 'fit-content',
                            }}
                            center="all"
                        >
                            {StatusDisplayStyle.name}
                        </Box>
                    );
                },
            },
        ],
        [users],
    );

    const SelectedOrderIndex = orders?.findIndex((order) => order.id === selectedId);

    if (!orders || orders.length === 0) {
        return (
            <Box center="all" h={250}>
                <Typography variant="heading4">There are currently no orders to display.</Typography>
            </Box>
        );
    }
    return (
        <StyledTable
            {...props}
            columns={columns}
            data={orders}
            variant="contrast"
            selectedIndex={SelectedOrderIndex}
            onRowClick={(row: Order, idx: number) => {
                setSelectedId(row.id);

                if (props.onRowClick) {
                    props.onRowClick(row, idx);
                }
            }}
        />
    );
};

const mapStateToProps = (state: RootState) => {
    return {};
};

export default connect(mapStateToProps, noop)(ExternalOrderDashboardTable);
